/**
 * ValueSpotlights — editorial cards sourced from /api/value/spotlights.
 *
 * Logged-out visitors see three cards: *Best on a budget*, *Best
 * all-rounder*, *Best 10-year ROI*. Signed-in students see one
 * personalised pick keyed to their inferred fee-status map.
 *
 * The component silently returns null if the fetch fails — this is a
 * marketing block, not a critical surface.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, TrendingUp, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

const ANGLE_ICON = {
  "Best on a budget": Wallet,
  "Best all-rounder": Sparkles,
  "Best 10-year ROI": TrendingUp,
  "Best for your profile": Sparkles,
};

export default function ValueSpotlights({ testId = "value-spotlights" }) {
  const { formatFrom } = useCurrency();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/value/spotlights")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.picks?.length) return null;

  return (
    <section
      className="max-w-6xl mx-auto px-4 py-10 md:py-14"
      data-testid={testId}
    >
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] font-underground-sc tracking-[0.18em] text-amber-700 mb-1">
            Best-value spotlights
          </div>
          <h2 className="font-editorial text-3xl md:text-4xl text-stone-900 leading-tight">
            {data.personalised
              ? "Where your money goes furthest"
              : "Where your money goes furthest, globally"}
          </h2>
          <p className="text-[14px] text-stone-600 mt-2 max-w-xl">
            {data.personalised
              ? "Picked for your inferred fee status, degree subject and residency."
              : "Three editorial picks across the cost ladder from our 22-country league table."}
          </p>
        </div>
        <Link
          to="/value/countries"
          className="text-[13px] font-semibold text-[#4C1D95] hover:text-[#2E1065] inline-flex items-center gap-1"
          data-testid={`${testId}-see-all`}
        >
          See the full ranking <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className={`grid gap-5 ${data.picks.length === 1 ? "md:grid-cols-1" : "md:grid-cols-3"}`}>
        {data.picks.map((p) => (
          <SpotlightCard
            key={`${p.angle}-${p.country.code}`}
            pick={p}
            formatFrom={formatFrom}
            testId={`${testId}-card-${p.country.code}`}
          />
        ))}
      </div>
    </section>
  );
}

function SpotlightCard({ pick, formatFrom, testId }) {
  const c = pick.country;
  const Icon = ANGLE_ICON[pick.angle] || Sparkles;
  const isHome = c.status === "home";
  return (
    <Link
      to={`/value/countries`}
      data-testid={testId}
      className="group rounded-2xl border border-stone-200 bg-white p-5 hover:border-[#4C1D95] hover:shadow-lg transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-amber-600" />
          <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
            {pick.angle}
          </div>
        </div>
        {isHome && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase tracking-wider">
            Home fee
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="text-4xl">{c.flag}</div>
        <div>
          <div className="font-editorial text-2xl text-stone-900 leading-tight">
            {c.name}
          </div>
          <div className="text-[11.5px] text-stone-500 mt-0.5 line-clamp-1">
            {c.tags?.slice(0, 2).join(" · ")}
          </div>
        </div>
      </div>

      <p className="text-[12.5px] text-stone-700 leading-snug mb-4">
        {pick.angle_blurb}
      </p>

      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-stone-200">
        <StatMini label="Total cost" value={formatFrom(c.total_cost_gbp, "GBP")} />
        <StatMini label="10-yr ROI"  value={formatFrom(c.net_roi_10yr_gbp, "GBP")} positive={c.net_roi_10yr_gbp > 0} />
        <StatMini label="Payback"    value={c.payback_years ? `${c.payback_years} yrs` : "—"} />
      </div>

      <div className="mt-3 text-[12px] font-semibold text-[#4C1D95] inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
        See the breakdown <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}

function StatMini({ label, value, positive }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-400">{label}</div>
      <div className={`text-[12px] tabular-nums font-semibold ${positive ? "text-emerald-700" : "text-stone-800"}`}>
        {value}
      </div>
    </div>
  );
}
